import React, { createContext, useContext, useState, useEffect, useRef, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TimeTrackingContext = createContext(null);

export const TimeTrackingProvider = ({ children }) => {
  const [activeTimer, setActiveTimer] = useState(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const timerIntervalRef = useRef(null);
  const pollingIntervalRef = useRef(null);

  const token = localStorage.getItem('token');
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  // Calculate elapsed seconds from start_time
  const calculateElapsedSeconds = useCallback((startTime) => {
    if (!startTime) return 0;
    const start = new Date(startTime);
    const now = new Date();
    return Math.max(0, Math.floor((now - start) / 1000));
  }, []);

  // Fetch active timer from server
  const checkActiveTimer = useCallback(async () => {
    if (!token) {
      setIsLoading(false);
      return null;
    }
    try {
      const res = await axios.get(`${API_URL}/api/time-entries/active`, { headers });
      if (res.data) {
        setActiveTimer(res.data);
        const elapsed = calculateElapsedSeconds(res.data.start_time);
        setElapsedSeconds(elapsed);
        return res.data;
      } else {
        setActiveTimer(null);
        setElapsedSeconds(0);
        return null;
      }
    } catch (err) {
      console.error('Error checking active timer', err);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [token, calculateElapsedSeconds, headers]);

  // Start a timer for a task
  const startTimer = useCallback(async (taskId) => {
    if (!taskId) {
      toast.error('Bitte wählen Sie einen Task aus');
      return null;
    }
    try {
      const res = await axios.post(`${API_URL}/api/time-entries/start?task_id=${taskId}`, {}, { headers });
      setActiveTimer(res.data);
      setElapsedSeconds(0);
      // Dispatch global event for any legacy listeners
      window.dispatchEvent(new CustomEvent('timerStarted', { detail: res.data }));
      toast.success('Timer gestartet');
      return res.data;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Starten des Timers');
      return null;
    }
  }, [headers]);

  // Stop the active timer
  const stopTimer = useCallback(async () => {
    try {
      await axios.post(`${API_URL}/api/time-entries/stop`, {}, { headers });
      setActiveTimer(null);
      setElapsedSeconds(0);
      // Dispatch global event for any legacy listeners
      window.dispatchEvent(new CustomEvent('timerStopped'));
      toast.success('Timer gestoppt');
      return true;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Stoppen des Timers');
      return false;
    }
  }, [headers]);

  // Initial load - check for active timer
  useEffect(() => {
    checkActiveTimer();
  }, [checkActiveTimer]);

  // Timer tick effect - increment elapsed seconds every second when timer is active
  useEffect(() => {
    if (activeTimer) {
      // Clear any existing interval
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
      
      timerIntervalRef.current = setInterval(() => {
        setElapsedSeconds(prev => prev + 1);
      }, 1000);
    } else {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
    }

    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
    };
  }, [activeTimer]);

  // Periodic polling to sync state with server (fallback for multi-tab scenarios)
  useEffect(() => {
    if (token) {
      pollingIntervalRef.current = setInterval(() => {
        checkActiveTimer();
      }, 30000); // Poll every 30 seconds
    }

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, [token, checkActiveTimer]);

  // Listen for global timer events (for legacy components or external triggers)
  useEffect(() => {
    const handleTimerStarted = () => checkActiveTimer();
    const handleTimerStopped = () => {
      setActiveTimer(null);
      setElapsedSeconds(0);
    };

    window.addEventListener('timerStarted', handleTimerStarted);
    window.addEventListener('timerStopped', handleTimerStopped);

    return () => {
      window.removeEventListener('timerStarted', handleTimerStarted);
      window.removeEventListener('timerStopped', handleTimerStopped);
    };
  }, [checkActiveTimer]);

  // Format elapsed time as HH:MM:SS
  const formatElapsed = useCallback((seconds) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  }, []);

  const value = {
    activeTimer,
    elapsedSeconds,
    isLoading,
    startTimer,
    stopTimer,
    checkActiveTimer,
    formatElapsed,
  };

  return (
    <TimeTrackingContext.Provider value={value}>
      {children}
    </TimeTrackingContext.Provider>
  );
};

export const useTimeTracking = () => {
  const context = useContext(TimeTrackingContext);
  if (!context) {
    throw new Error('useTimeTracking must be used within a TimeTrackingProvider');
  }
  return context;
};

export default TimeTrackingContext;
